import { api } from './api';
import type { Chain, Token } from './transfers';

export type SwapStatus =
  | 'WAITING'
  | 'CONFIRMING'
  | 'EXCHANGING'
  | 'SENDING'
  | 'FINISHED'
  | 'FAILED'
  | 'REFUNDED'
  | 'VERIFYING'
  | string;

export interface SwapOrderDto {
  id: string;
  partnerOrderId: string;
  provider: string;
  fromChain: Chain;
  fromToken: Token;
  toChain: Chain;
  toToken: Token;
  fromCurrency: string;
  toCurrency: string;
  fromAmount: string;
  toAmount: string | null;
  payinAddress: string | null;
  payoutAddress: string;
  refundAddress: string;
  status: SwapStatus;
  providerOrderId: string | null;
  statusReason: string | null;
  payinExtraId: string | null;
  depositTransferId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SwapEstimate {
  from: { chain: Chain; token: Token; currency: string };
  to: { chain: Chain; token: Token; currency: string };
  fromAmount: string;
  toAmount: string;
  networkFee: string | null;
  minAmount: number;
  maxAmount: number | null;
  rate: string | null;
  provider: string;
}

export interface SwapAssetDto {
  chain: Chain;
  token: Token;
  currency: string;
  network: string;
  isNative: boolean;
  tokenContract: string | null;
  decimals: number;
  label: string;
  extraIdName: string | null;
}

/** Stable key for a provider-traded token × chain. */
export const swapAssetKey = (a: { chain: string; token: string }) => `${a.token}-${a.chain}`;

export async function fetchSwapConfig(initData: string): Promise<{ enabled: boolean }> {
  try {
    return await api<{ enabled: boolean }>(initData, '/v1/swap/config');
  } catch {
    return { enabled: false };
  }
}

/** Live ChangeHero ∩ allowlist assets — refresh/cached via react-query on the caller. */
export async function fetchSwapAssets(
  initData: string,
): Promise<{ assets: SwapAssetDto[]; cacheTtlSec: number }> {
  return api<{ assets: SwapAssetDto[]; cacheTtlSec: number }>(initData, '/v1/swap/assets');
}

export async function estimateSwap(
  initData: string,
  body: {
    fromChain: Chain;
    fromToken: Token;
    toChain: Chain;
    toToken: Token;
    fromAmount: string;
  },
): Promise<SwapEstimate> {
  return api<SwapEstimate>(initData, '/v1/swap/estimate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function createSwapOrder(
  initData: string,
  body: {
    fromChain: Chain;
    fromToken: Token;
    toChain: Chain;
    toToken: Token;
    fromAmount: string;
  },
): Promise<SwapOrderDto> {
  return api<SwapOrderDto>(initData, '/v1/swap/order', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function fetchSwapOrder(initData: string, partnerOrderId: string): Promise<SwapOrderDto> {
  return api<SwapOrderDto>(initData, `/v1/swap/order/${partnerOrderId}`);
}

export async function fetchSwapOrders(
  initData: string,
  filter?: { chain?: Chain; token?: Token },
): Promise<SwapOrderDto[]> {
  const qs = new URLSearchParams();
  if (filter?.chain) qs.set('chain', filter.chain);
  if (filter?.token) qs.set('token', filter.token);
  const q = qs.toString();
  const res = await api<{ orders: SwapOrderDto[] }>(
    initData,
    `/v1/swap/orders${q ? `?${q}` : ''}`,
  );
  return res.orders;
}

export async function linkSwapDeposit(
  initData: string,
  partnerOrderId: string,
  depositTransferId: string,
): Promise<SwapOrderDto> {
  return api<SwapOrderDto>(initData, `/v1/swap/order/${partnerOrderId}/deposit`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ depositTransferId }),
  });
}

export function isSwapTerminal(status: string): boolean {
  return status === 'FINISHED' || status === 'FAILED' || status === 'REFUNDED';
}

export function isSwapPending(status: string): boolean {
  return !isSwapTerminal(status);
}
